/**
 * Kompakter Audio-Player für Lead Sheets.
 *
 * Eine Referenzaufnahme hängt direkt am Song (Song.audio, siehe types.ts).
 * Wiedergabe, Wellenform und das Einlesen der Datei nutzen dieselben
 * Bausteine wie die Tonaufnahmen auf einer Bibel-Fläche
 * (features/canvas/board-canvas.ts, features/canvas/audio.ts) – keine
 * zweite Dekodier- oder Wellenform-Logik.
 *
 * Neu ist hier nur, was ein Lead Sheet zusätzlich braucht: Sperren/
 * Entsperren, damit die Aufnahme nicht aus Versehen beim Bearbeiten
 * verschwindet, und "Automatisch abspielen" je Song.
 */
import type { Song, SongAudio } from '../types.ts';
import {
  AUDIO_ACCEPT,
  formatTime,
  paintProgress,
  readAudio,
  seekRatio,
  waveformSvg,
} from '../features/canvas/audio.ts';
import { h } from './dom.ts';
import { icon } from './icons.ts';
import { toast } from './toast.ts';

export interface AudioPanelOptions {
  song: Song;
  /** Zeigt bei fehlender Aufnahme einen Knopf zum Hinzufügen. */
  allowAdd: boolean;
  /** Persistiert eine Änderung (inkl. Entfernen, dann undefined). */
  onChange: (audio: SongAudio | undefined) => void | Promise<void>;
}

export interface AudioPanel {
  element: HTMLElement;
  hasAudio(): boolean;
  /**
   * Versucht, die Aufnahme automatisch zu starten, wenn ihre Autoplay-
   * Einstellung an ist. Erlaubt der Browser das nicht ohne vorherige
   * Interaktion, bleibt es beim sauberen Fallback: die Aufnahme steht
   * bereit, ein Tipper auf Abspielen reicht.
   */
  tryAutoplay(): void;
  destroy(): void;
}

function warnIfLarge(src: string): void {
  if (src.length > 8_000_000) toast('Große Aufnahme – das Backup wird dadurch dick.');
}

/** Öffnet die Dateiauswahl und liefert die gewählte Datei. */
function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = h('input', { attrs: { type: 'file', accept }, style: { display: 'none' } });
    input.addEventListener('change', () => {
      const file = input.files?.[0] ?? null;
      input.remove();
      resolve(file);
    });
    document.body.append(input);
    input.click();
  });
}

export function createAudioPanel(opts: AudioPanelOptions): AudioPanel {
  let audio: SongAudio | undefined = opts.song.audio;

  const media = new Audio();
  media.preload = 'metadata';
  if (audio) media.src = audio.src;

  /* --------------------------------------------------------- Leerer Zustand */

  const addBtn = h(
    'button',
    { class: 'audio-panel__add', attrs: { type: 'button' }, on: { click: () => void addNew() } },
    icon('music'),
    h('span', { text: 'Audio hinzufügen' }),
  );
  const empty = h('div', { class: 'audio-panel audio-panel--empty' }, addBtn);

  /* ------------------------------------------------------- Voller Zustand */

  let waveEl = waveformSvg(audio?.peaks ?? []);
  waveEl.classList.add('audio-panel__wave');

  function wireWave(svg: SVGSVGElement): void {
    svg.addEventListener('pointerdown', (ev) => {
      ev.stopPropagation();
      const dur = media.duration || audio?.duration || 0;
      if (dur > 0) media.currentTime = seekRatio(svg, ev.clientX) * dur;
    });
  }
  wireWave(waveEl);

  /** Baut die Wellenform neu, sobald eine andere Aufnahme liegt. */
  function refreshWave(): void {
    const next = waveformSvg(audio?.peaks ?? []);
    next.classList.add('audio-panel__wave');
    wireWave(next);
    waveEl.replaceWith(next);
    waveEl = next;
  }

  const timeEl = h('span', { class: 'audio-panel__time' });

  const playBtn = h(
    'button',
    { class: 'audio-panel__play', attrs: { type: 'button', 'aria-label': 'Abspielen' } },
    icon('play'),
  );

  const lockBtn = h('button', {
    class: 'audio-panel__icon',
    attrs: { type: 'button' },
    on: { click: () => void toggleLock() },
  });

  const menuBtn = h(
    'button',
    {
      class: 'audio-panel__icon',
      attrs: { type: 'button', title: 'Mehr', 'aria-label': 'Mehr' },
      on: { click: () => setMenu(menu.hidden) },
    },
    icon('dots'),
  );

  const autoplayInput = h('input', { attrs: { type: 'checkbox' } });
  autoplayInput.addEventListener('change', () => void setAutoplay(autoplayInput.checked));

  const replaceBtn = h(
    'button',
    { class: 'audio-menu__item', attrs: { type: 'button' }, on: { click: () => void addNew() } },
    icon('music'),
    h('span', { text: 'Ersetzen' }),
  );
  const removeBtn = h(
    'button',
    {
      class: 'audio-menu__item audio-menu__item--danger',
      attrs: { type: 'button' },
      on: { click: () => void remove() },
    },
    icon('trash'),
    h('span', { text: 'Entfernen' }),
  );

  const menu = h(
    'div',
    { class: 'audio-menu', attrs: { hidden: 'hidden', role: 'menu' } },
    h(
      'label',
      { class: 'audio-menu__item audio-menu__toggle' },
      h('span', { text: 'Beim Öffnen automatisch abspielen' }),
      autoplayInput,
    ),
    replaceBtn,
    removeBtn,
  );

  const bar = h('div', { class: 'audio-panel__bar' }, playBtn, waveEl, timeEl, lockBtn, menuBtn);
  const full = h('div', { class: 'audio-panel', attrs: { hidden: 'hidden' } }, bar, menu);

  const wrap = h('div', { class: 'audio-panel-wrap' }, empty, full);

  /* ---------------------------------------------------------------- Malen */

  function paintPlay(): void {
    const playing = !media.paused && !media.ended;
    playBtn.textContent = '';
    playBtn.append(icon(playing ? 'pause' : 'play'));
    playBtn.setAttribute('aria-label', playing ? 'Pause' : 'Abspielen');
  }

  function paintLock(): void {
    const locked = audio?.locked ?? false;
    lockBtn.textContent = '';
    lockBtn.append(icon(locked ? 'lock' : 'unlock'));
    lockBtn.classList.toggle('audio-panel__icon--on', locked);
    const label = locked ? 'Entsperren' : 'Sperren';
    lockBtn.setAttribute('title', label);
    lockBtn.setAttribute('aria-label', label);
    // Gesperrt heisst: nicht ersetzen, nicht entfernen. Abspielen und
    // Suchen in der Wellenform bleiben immer erlaubt.
    replaceBtn.toggleAttribute('disabled', locked);
    removeBtn.toggleAttribute('disabled', locked);
  }

  function paintTime(): void {
    const dur = audio?.duration || media.duration || 0;
    timeEl.textContent = `${formatTime(media.currentTime)} / ${formatTime(dur)}`;
  }

  function paint(): void {
    empty.hidden = !!audio || !opts.allowAdd;
    full.hidden = !audio;
    if (!audio) return;
    bar.setAttribute('aria-label', audio.name || 'Aufnahme');
    paintLock();
    paintTime();
    autoplayInput.checked = audio.autoplay;
    refreshWave();
    paintProgress(waveEl, media.duration > 0 ? media.currentTime / media.duration : 0);
  }

  function setMenu(open: boolean): void {
    menu.hidden = !open;
  }

  /* -------------------------------------------------------------- Wiedergabe */

  function togglePlay(): void {
    if (media.paused) void media.play().catch(() => toast('Wiedergabe nicht möglich'));
    else media.pause();
  }
  playBtn.addEventListener('click', togglePlay);

  media.addEventListener('play', paintPlay);
  media.addEventListener('pause', paintPlay);
  media.addEventListener('ended', () => {
    paintProgress(waveEl, 0);
    paintPlay();
    paintTime();
  });
  media.addEventListener('timeupdate', () => {
    if (media.duration > 0) paintProgress(waveEl, media.currentTime / media.duration);
    paintTime();
  });
  media.addEventListener('loadedmetadata', () => {
    if (audio && !audio.duration && Number.isFinite(media.duration)) {
      audio = { ...audio, duration: media.duration };
      void opts.onChange(audio);
    }
    paintTime();
  });

  /* --------------------------------------------------------------- Aktionen */

  async function persist(next: SongAudio | undefined): Promise<void> {
    audio = next;
    media.pause();
    media.src = next?.src ?? '';
    paint();
    await opts.onChange(next);
  }

  async function addNew(): Promise<void> {
    if (audio?.locked) {
      toast('Erst entsperren');
      return;
    }
    const file = await pickFile(AUDIO_ACCEPT);
    if (!file) return;
    toast('Aufnahme wird gelesen …');
    try {
      const info = await readAudio(file);
      warnIfLarge(info.src);
      const next: SongAudio = {
        src: info.src,
        name: info.name,
        duration: info.duration,
        peaks: info.peaks,
        // Sperr- und Autoplay-Einstellung bleiben beim Ersetzen erhalten.
        locked: audio?.locked ?? false,
        autoplay: audio?.autoplay ?? false,
      };
      await persist(next);
      setMenu(false);
      toast(info.peaks.length === 0 ? 'Eingefügt – Wellenform nicht möglich' : 'Audio eingefügt');
    } catch {
      toast('Diese Datei konnte nicht gelesen werden');
    }
  }

  async function remove(): Promise<void> {
    if (audio?.locked) {
      toast('Erst entsperren');
      return;
    }
    if (!confirm('Audio entfernen?')) return;
    setMenu(false);
    await persist(undefined);
    toast('Audio entfernt');
  }

  async function toggleLock(): Promise<void> {
    if (!audio) return;
    await persist({ ...audio, locked: !audio.locked });
  }

  async function setAutoplay(value: boolean): Promise<void> {
    if (!audio) return;
    await persist({ ...audio, autoplay: value });
  }

  const onOutside = (ev: PointerEvent) => {
    if (menu.hidden) return;
    if (menu.contains(ev.target as Node) || menuBtn.contains(ev.target as Node)) return;
    setMenu(false);
  };
  document.addEventListener('pointerdown', onOutside);

  paint();

  return {
    element: wrap,
    hasAudio: () => !!audio,
    tryAutoplay() {
      if (!audio?.autoplay) return;
      // Kein Hack: einfach versuchen. Verweigert iPadOS/Safari den Start
      // ohne vorherige Beruehrung, bleibt die Aufnahme sauber pausiert –
      // ein Tipper auf Abspielen reicht dann.
      media.play().catch(() => {
        /* sauberer Fallback: pausiert stehen lassen, keine Wiederholversuche */
      });
    },
    destroy() {
      media.pause();
      media.src = '';
      document.removeEventListener('pointerdown', onOutside);
    },
  };
}
